import { Button, buttonVariants, cn } from "@safeplate/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Printer } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { displayError } from "../api/errors.js";
import { useDocumentTitle } from "../components/document-title.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { assetResource, type SetupItem } from "../setup/resources.js";
import { getAsset, issueAssetQr, listSetupItems } from "../setup/setup-api.js";
import { useShell } from "../shell/shell-context.js";
import {
  labelColours,
  storeLabelColour,
  storedLabelColour,
  wantsLabel,
  type LabelColour,
} from "./label-colours.js";
import { assetQrUrl, qrPath } from "./qr-path.js";

function QrCode({
  ink,
  label,
  text,
}: {
  ink: string;
  label: string;
  text: string;
}) {
  const { path, size } = qrPath(text);
  return (
    <svg
      aria-label={label}
      className="size-[34mm] shrink-0"
      role="img"
      shapeRendering="crispEdges"
      viewBox={`0 0 ${size} ${size}`}
    >
      <rect fill="#fff" height={size} width={size} />
      <path d={path} fill={ink} />
    </svg>
  );
}

// One printable label: the asset's name, its location and a QR code a phone
// scans to log it. The code always sits on white with its quiet zone.
function AssetLabel({
  colour,
  location,
  name,
  url,
}: {
  colour: LabelColour;
  location: string | undefined;
  name: string;
  url: string;
}) {
  return (
    <div
      className="flex w-[90mm] items-center gap-[4mm] rounded-sm border-[0.8mm] bg-white p-[4mm] text-black print:break-inside-avoid"
      style={{ borderColor: colour.ink }}
    >
      <QrCode ink={colour.ink} label={`QR code for ${name}`} text={url} />
      <div className="min-w-0">
        <p
          className="text-[5mm] leading-tight font-bold break-words"
          style={{ color: colour.ink }}
        >
          {name}
        </p>
        {location ? (
          <p className="mt-[1.5mm] text-[3.2mm] leading-snug">{location}</p>
        ) : null}
        <p className="mt-[3mm] text-[2.8mm] font-semibold tracking-wide">
          SafePlate
        </p>
      </div>
    </div>
  );
}

function ColourPicker({
  onChange,
  value,
}: {
  onChange: (colour: LabelColour) => void;
  value: LabelColour;
}) {
  return (
    <div
      aria-label="Label colour"
      className="flex items-center gap-2 print:hidden"
      role="group"
    >
      {labelColours.map((colour) => (
        <button
          aria-label={colour.name}
          aria-pressed={colour.key === value.key}
          className={cn(
            "size-9 rounded-full border-4 border-white shadow-sm ring-2 transition outline-none focus-visible:ring-emerald-700",
            colour.key === value.key ? "ring-stone-900" : "ring-stone-200",
          )}
          key={colour.key}
          onClick={() => onChange(colour)}
          style={{ backgroundColor: colour.ink }}
          type="button"
        />
      ))}
    </div>
  );
}

function useLabelColour() {
  const [colour, setColour] = useState(storedLabelColour);
  return [
    colour,
    (next: LabelColour) => {
      storeLabelColour(next);
      setColour(next);
    },
  ] as const;
}

function LabelSheet({
  children,
  colour,
  onColour,
}: {
  children: ReactNode;
  colour: LabelColour;
  onColour: (colour: LabelColour) => void;
}) {
  return (
    <main className="min-h-dvh bg-canvas px-5 py-10 print:bg-white print:p-0">
      <div className="mx-auto flex max-w-[190mm] flex-col items-center gap-6 print:block">
        <div className="flex flex-wrap items-center justify-center gap-4 print:hidden">
          <ColourPicker onChange={onColour} value={colour} />
          <Button onClick={() => window.print()}>
            <Printer aria-hidden="true" className="size-4" /> Print
          </Button>
        </div>
        {children}
      </div>
    </main>
  );
}

function Centred({ children }: { children: ReactNode }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-canvas p-5">
      <div className="w-full max-w-xl">{children}</div>
    </main>
  );
}

export function AssetLabelPage({
  assetId,
  token,
}: {
  assetId: string;
  token?: string;
}) {
  const shell = useShell();
  const [colour, setColour] = useLabelColour();
  const asset = useQuery({
    queryFn: () => getAsset(assetId, shell.activeOrganisationId),
    queryKey: ["asset", assetId, shell.activeOrganisationId],
  });
  // A fridge without a code gets one the first time its label opens.
  const issue = useMutation({ mutationFn: () => issueAssetQr(assetId) });
  const issuing = useRef(false);
  const needsCode = Boolean(asset.data && !asset.data.qr_url && !token);
  useEffect(() => {
    if (!needsCode || issuing.current) return;
    issuing.current = true;
    issue.mutate();
  }, [issue, needsCode]);
  useDocumentTitle(asset.data ? `${asset.data.name} label` : "Label");

  if (asset.isPending || (needsCode && !issue.data && !issue.error))
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        <LoadingState label="Loading the label" />
      </main>
    );
  const url = asset.data
    ? assetQrUrl(asset.data.qr_url ?? issue.data?.qr_url, token)
    : null;
  const failure = asset.error ?? issue.error;
  if (failure || !asset.data || !url)
    return (
      <Centred>
        <ErrorState
          message={failure ? displayError(failure) : "This label has no code"}
          onAction={() => void asset.refetch()}
          title="Couldn’t make the label"
        />
      </Centred>
    );

  const location = shell.locations.find(
    (entry) => entry.id === asset.data.location_id,
  );

  return (
    <LabelSheet colour={colour} onColour={setColour}>
      <AssetLabel
        colour={colour}
        location={location?.name}
        name={asset.data.name}
        url={url}
      />
    </LabelSheet>
  );
}

// Every fridge's label at one location, two to a row on A4. Fridges without a
// code get one first; codes already on fridges stay as they are.
export function AssetLabelsPage({ locationId }: { locationId: string }) {
  const shell = useShell();
  const [colour, setColour] = useLabelColour();
  const labels = useQuery({
    queryFn: async () => {
      const items = (await listSetupItems(assetResource, locationId)).filter(
        wantsLabel,
      );
      const ready: { item: SetupItem; url: string }[] = [];
      for (const item of items) {
        const existing =
          typeof item.qr_url === "string" && item.qr_url ? item.qr_url : null;
        const url = existing ?? (await issueAssetQr(item.id)).qr_url;
        ready.push({ item, url });
      }
      return ready;
    },
    queryKey: ["asset-labels", locationId],
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
  const location = shell.locations.find((entry) => entry.id === locationId);
  useDocumentTitle(location ? `${location.name} labels` : "Labels");

  if (labels.isPending)
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        <LoadingState label="Making the labels" />
      </main>
    );
  if (labels.error)
    return (
      <Centred>
        <ErrorState
          message={displayError(labels.error)}
          onAction={() => void labels.refetch()}
          title="Couldn’t make the labels"
        />
      </Centred>
    );
  if (labels.data.length === 0)
    return (
      <Centred>
        <EmptyState
          action={
            <Link
              className={buttonVariants()}
              search={{ section: "equipment" }}
              to="/setup"
            >
              Add a fridge
            </Link>
          }
          title="No fridges yet"
        />
      </Centred>
    );

  return (
    <LabelSheet colour={colour} onColour={setColour}>
      <div className="grid gap-[4mm] sm:grid-cols-2 print:grid-cols-2">
        {labels.data.map(({ item, url }) => (
          <AssetLabel
            colour={colour}
            key={item.id}
            location={location?.name}
            name={typeof item.name === "string" ? item.name : "Fridge"}
            url={url}
          />
        ))}
      </div>
    </LabelSheet>
  );
}
